import AddIcon from "@mui/icons-material/Add";
import Can from "../../casl/Can";
import DataTable from "../shared/table";
import Header from "../shared/header";
import LinkButton from "../shared/link-button";
import React, { useEffect, useState, FunctionComponent } from "react";
import useBreadcrumbStore from "../../stores/store-breadcrumbs";
import { Box } from "@mui/material";
import { Outlet } from "react-router-dom";
import { useFetchProducts } from "../../api/product";
import { columns } from "./products.columns";
import {
  GridPaginationModel,
  GridSortItem,
} from "@mui/x-data-grid";
import useFiltersStore from "../../stores/store-filters";
import useSearchStore from "../../stores/store-query-filters";
import SearchBar from "../shared/search-bar";
/**
 * Interface for ProductsList component props
 */
interface ProductsListProps {}

/**
 * ProductsList Component
 * 
 * This React functional component displays a paginated, sortable and filterable list of products.
 * It provides functionality for viewing, creating and managing products with appropriate permissions.
 *
 * @remarks
 * The component:
 * - Handles server-side pagination, filtering and sorting
 * - Manages breadcrumb navigation
 * - Provides product creation functionality for authorized users
 * - Renders products in a data table with configurable columns
 * - Handles loading and error states
 *
 * @component
 * @example
 * ```tsx
 * <ProductsList />
 * ```
 * 
 * @returns {JSX.Element} Product list page with header, data table and creation button
 */
const ProductsList: FunctionComponent<ProductsListProps> = () => {
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  // Filter
  const filterKey = 'products';
  const filter = useFiltersStore((state) => state.getFilterObject(filterKey));
  // Sort
  const [sort, setSort] = useState({});
  
 // Search — key scopes this query to the products list in the global store
  const searchKey = 'products';
  const searchQuery = useSearchStore((state) => state.getSearch(searchKey));
  const {
    data: productsData,
    isLoading,
    isError,
} = useFetchProducts(searchQuery.trim(), itemsPerPage, currentPage, undefined, sort);

  const products = productsData?.data; // Extract data from the 'data' property
  const productsTotalItems = productsData?.pager?.total;

  // reset the breadcrumb
  const resetBreadcrumb = useBreadcrumbStore((state) => state.resetBreadcrumb);

  const handlePageChange = (newPage: GridPaginationModel) => {
    // Fetch new data from the server based on the new page
    setCurrentPage(newPage.page + 1);
    setItemsPerPage(newPage.pageSize);
  };

  useEffect(() => {
    resetBreadcrumb();
  }, [resetBreadcrumb]);

  const handleSortModelChange = (sortItem: GridSortItem) => {
    setSort(sortItem);
  };


  return (
    <>
      {!isError && (
        <Box
          sx={{
            marginTop: "60px",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
            flexDirection={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "flex-start", lg: "flex-end" }}
          >
            <Header title="Produkte" />
            <Can I="create" a="Product">
              <LinkButton
                label="Produkt erstellen"
                startIcon={<AddIcon />}
                to="/products/create"
              />
            </Can>
          </Box>
          <Can I="view" a="Products">
            <Box marginTop="20px" display="flex" flexDirection="column" height="70%">
                <Box mb={1}>
    <SearchBar searchKey={searchKey} onSearch={() => setCurrentPage(1)} />
  </Box>

              <DataTable
                filterKey={filterKey}
                parentPath="products"
                data={products ?? []}
                fieldsNames={columns}
                minHeight={600}
                onPageChange={handlePageChange}
                currentPage={currentPage - 1}
                totalItems={productsTotalItems}
                itemsPerPage={itemsPerPage}
                ssrFilter={true}
                isLoading={isLoading}
                onSortChange={handleSortModelChange}
                ssrSort={true}
              />
            </Box>
          </Can>
          <Outlet />
        </Box>
      )}
    </>
  );
};

export default ProductsList;
